import {useState} from 'react';
import {SubmitHandler, useForm, UseFormReturn} from 'react-hook-form';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {t} from '@lingui/core/macro';

import {tagsAPI} from 'entities/monitor';
import {IPortMonitorMutationError, portMonitorAPI} from 'features/port/api/portMonitorAPI';
import {mapPortMonitorViolations} from 'features/port/lib/mapPortMonitorViolations';
import {
    buildPortMonitorInput,
    getPortMonitorFormDefaults,
    getPortMonitorNamePlaceholder,
} from 'features/port/lib/portMonitorFormHelper';
import {isPortMonitorLimitViolation} from 'features/port/lib/portMonitorHelper';
import {useIsFreeTariff, fetchMe, userSelector} from 'entities/user';
import {TypedDispatch} from 'shared/model/types';

import CreatePortMonitorType from './PortMonitor/CreatePortMonitorType';
import IPortMonitor from './PortMonitor/IPortMonitor';

interface IUsePortMonitorFormParams {
    item?: IPortMonitor,
    onSuccess: () => void,
    onLimitExceeded?: () => void,
}

export interface IUsePortMonitorForm {
    form: UseFormReturn<CreatePortMonitorType>,
    isNew: boolean,
    isSubmitting: boolean,
    fieldErrors: Record<string, string>,
    commonError: string | undefined,
    namePlaceholder: string,
    submit: ReturnType<UseFormReturn<CreatePortMonitorType>['handleSubmit']>,
}

export const usePortMonitorForm = ({
    item,
    onSuccess,
    onLimitExceeded,
}: IUsePortMonitorFormParams): IUsePortMonitorForm => {
    const user = useSelector(userSelector);
    const dispatch = useDispatch<TypedDispatch>();
    const freeTariff = useIsFreeTariff();

    const [addPortMonitor, {isLoading: isLoadingCreate}] = portMonitorAPI.useAddPortMonitorMutation();
    const [updatePortMonitor, {isLoading: isLoadingUpdate}] = portMonitorAPI.useUpdatePortMonitorMutation();
    const {refetch: refetchTags} = tagsAPI.useGetTagsQuery();

    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [commonError, setCommonError] = useState<string>();

    const isNew = !item;

    const form = useForm<CreatePortMonitorType>({
        mode: 'onChange',
        defaultValues: getPortMonitorFormDefaults(item, {
            freeTariff,
            emailConfirmed: !!user?.email_confirmed,
        }),
    });

    const namePlaceholder = getPortMonitorNamePlaceholder(form.watch('ip'), form.watch('port'));

    const onSubmit: SubmitHandler<CreatePortMonitorType> = async(values) => {
        setFieldErrors({});
        setCommonError(undefined);

        const data = buildPortMonitorInput(values);

        try {
            if (item) {
                await updatePortMonitor({uuid: item.uuid, ...data}).unwrap();
                toast(t`Монитор порта изменён`);
            } else {
                await addPortMonitor(data).unwrap();
                toast(t`Монитор порта добавлен`);
            }
            await refetchTags();
            dispatch(fetchMe());
            onSuccess();
        } catch (error) {
            const violations = (error as IPortMonitorMutationError)?.violations;
            const {fields, common} = mapPortMonitorViolations(violations);
            setFieldErrors(fields);
            setCommonError(common);

            if (isNew && isPortMonitorLimitViolation(violations)) {
                onLimitExceeded?.();
            }
        }
    };

    return {
        form,
        isNew,
        isSubmitting: isLoadingCreate || isLoadingUpdate,
        fieldErrors,
        commonError,
        namePlaceholder,
        submit: form.handleSubmit(onSubmit),
    };
};

export default usePortMonitorForm;
